import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { relativeSeen, DEVICE_TONE } from './format';

export default function DevicesTab() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        axiosInstance.get('/api/working-time/devices')
            .then((r) => setDevices(r.data.devices || []))
            .catch((e) => setError(e.response?.data?.message || 'Could not load devices'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const revoke = (device) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(
            `Revoke "${device.deviceName}"? The agent on that machine stops reporting `
            + 'immediately and the laptop must be re-enrolled to resume.'
        )) return;

        setBusyId(device._id);
        axiosInstance.patch(`/api/working-time/devices/${device._id}`, { isActive: false })
            .then(load)
            .catch((e) => setError(e.response?.data?.message || 'Could not revoke the device'))
            .finally(() => setBusyId(null));
    };

    const remove = (device) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(
            `Permanently delete "${device.deviceName}" and all of its recorded working time? `
            + 'This cannot be undone.'
        )) return;

        setBusyId(device._id);
        axiosInstance.delete(`/api/working-time/devices/${device._id}`)
            .then(load)
            .catch((e) => setError(e.response?.data?.message || 'Could not delete the device'))
            .finally(() => setBusyId(null));
    };

    return (
        <div>
            <div className="wt-toolbar">
                <button type="button" className="wt-btn" onClick={load}>Refresh</button>
            </div>

            {error && <div className="wt-error">{error}</div>}

            <div className="wt-table-wrap">
                <table className="wt-table">
                    <thead>
                        <tr>
                            <th>Employee</th><th>Device</th><th>Operating system</th>
                            <th>Agent</th><th>Last seen</th><th>State</th><th aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="wt-empty">Loading…</td></tr>}
                        {!loading && !devices.length && (
                            <tr>
                                <td colSpan={7} className="wt-empty">
                                    No devices enrolled yet. Run install.ps1 on an employee laptop to enroll it.
                                </td>
                            </tr>
                        )}
                        {!loading && devices.map((d) => (
                            <tr key={d._id}>
                                <td>
                                    <strong>{(d.user && d.user.fullName) || 'Unknown'}</strong>
                                    <span className="wt-sub">
                                        {[
                                            d.user && d.user.userId,
                                            d.user && d.user.department && d.user.department.name
                                        ].filter(Boolean).join(' · ')}
                                    </span>
                                </td>
                                <td><strong>{d.deviceName}</strong></td>
                                <td>{d.osVersion || '-'}</td>
                                <td className="wt-mono">{d.agentVersion ? `v${d.agentVersion}` : '-'}</td>
                                <td>{relativeSeen(d.minutesSinceSeen)}</td>
                                <td>
                                    <span className={`wt-badge ${DEVICE_TONE[d.status] || 'muted'}`}>
                                        {d.statusLabel}
                                    </span>
                                </td>
                                <td>
                                    {d.isActive ? (
                                        <button
                                            type="button"
                                            className="wt-btn"
                                            disabled={busyId === d._id}
                                            onClick={() => revoke(d)}
                                        >
                                            Revoke
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="wt-btn"
                                            disabled={busyId === d._id}
                                            onClick={() => remove(d)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="wt-note">
                A device shows as Offline when it has missed three heartbeats. Revoking stops it
                reporting but keeps its history; deleting removes the device and every day it recorded.
            </p>
        </div>
    );
}
